import { useEffect, useMemo, useState } from "react";
import { Clock, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { enGB, nl } from "date-fns/locale";
import { api } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { ptErrorMessage } from "../../lib/ptI18n";
import { Calendar } from "../../components/ui/calendar";

/* BOOK A SESSION — afgeschermde PT-agenda (alleen ingelogd, binnen klantdashboard) */
export default function PTBook({ pkg, onDone }) {
  const { t, lang } = useT();
  const dateLocale = lang === "en" ? enGB : nl;
  const [days, setDays] = useState(null);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [note, setNote] = useState("");
  const [usePackage, setUsePackage] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const refresh = () => api.get("/pt/slots?days=28").then(({ data }) => setDays(data.items || [])).catch(() => setDays([]));
  useEffect(() => { refresh(); }, []);

  const availableDates = useMemo(() => new Set((days || []).filter((d) => d.times.some((slot) => slot.available)).map((d) => d.date)), [days]);
  const selectedDay = (days || []).find((d) => d.date === date);

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !time) { toast.error(t("pt.book.chooseDateTime")); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/pt/bookings", { date, time, note, booking_type: usePackage && pkg ? "package" : "single" });
      setDone(data.booking);
      setTime("");
      refresh();
      onDone?.();
    } catch (err) {
      toast.error(ptErrorMessage(err?.response?.data?.detail, t) || t("pt.book.requestFailed"));
      refresh();
    }
    setBusy(false);
  };

  return (
    <div data-testid="pt-book">
      {pkg && (
        <div className="mb-6 inline-flex items-center gap-3 border px-4 py-3" style={{ borderColor: "var(--joh-accent)" }} data-testid="pt-package-banner">
          <span className="chip" style={{ color: "var(--joh-accent)" }}>PT {t("pt.common.package").toUpperCase()} — {t("pt.book.remaining").toUpperCase()}: {pkg.remaining}</span>
          <button type="button" onClick={() => setUsePackage(!usePackage)} className="chip px-3 py-1.5 border transition-colors" style={usePackage ? { background: "var(--joh-accent)", color: "#0A0A0A", borderColor: "var(--joh-accent)" } : { borderColor: "#F5F3F055" }} data-testid="pt-use-package">
            {usePackage ? t("pt.book.packageActive").toUpperCase() : t("pt.book.usePackage").toUpperCase()}
          </button>
        </div>
      )}
      <div className="grid lg:grid-cols-2 gap-8">
        <div>
          <div className="chip text-[#A3A3A3] mb-3">{t("pt.book.selectDate")}</div>
          <div className="border border-[#F5F3F0]/15 p-4 inline-block" data-testid="pt-calendar">
            <Calendar mode="single" selected={date ? new Date(date + "T00:00:00") : undefined}
              onSelect={(d) => { if (!d) return; const iso = format(d, "yyyy-MM-dd"); if (availableDates.has(iso)) { setDate(iso); setTime(""); setDone(null); } }}
              disabled={(d) => !availableDates.has(format(d, "yyyy-MM-dd"))}
              weekStartsOn={1} locale={dateLocale} />
          </div>
          {days !== null && availableDates.size === 0 && (
            <div className="chip text-[#4A4A4A] mt-4" data-testid="pt-no-slots">{t("pt.book.noSlots")}</div>
          )}
        </div>
        <div>
          <div className="chip text-[#A3A3A3] mb-3">{t("pt.book.selectTime")}{date ? ` — ${format(new Date(date + "T00:00:00"), "EEEE d MMMM", { locale: dateLocale })}` : ""}</div>
          {!date ? (
            <div className="border border-[#F5F3F0]/15 p-8 chip text-[#4A4A4A]" data-testid="pt-pick-date">{t("pt.book.pickDate")}</div>
          ) : (
            <div className="grid grid-cols-3 gap-2 mb-8" data-testid="pt-slots">
              {(selectedDay?.times || []).filter((slot) => slot.available).map((slot) => (
                <button type="button" key={slot.time} onClick={() => { setTime(slot.time); setDone(null); }}
                  className="py-3.5 chip border transition-colors inline-flex flex-col items-center justify-center"
                  style={time === slot.time ? { background: "var(--joh-accent)", color: "#0A0A0A", borderColor: "var(--joh-accent)" } : { borderColor: "#F5F3F033" }}
                  data-testid={`pt-slot-${slot.time}`}>
                  <span className="inline-flex items-center gap-1.5"><Clock size={12}/>{slot.time}</span>
                  <span className="text-[10px] opacity-60">{slot.time}–{`${String(parseInt(slot.time) + 1).padStart(2, "0")}:00`}</span>
                </button>
              ))}
              {(selectedDay?.times || []).filter((slot) => slot.available).length === 0 && (
                <div className="col-span-3 chip text-[#4A4A4A] py-4">{t("pt.book.fullyBooked")}</div>
              )}
            </div>
          )}
          {time && !done && (
            <form onSubmit={submit} className="border border-[#F5F3F0]/15 p-6 space-y-4" data-testid="pt-request-form">
              <div className="chip" style={{ color: "var(--joh-accent)" }}>{t("pt.book.requestSession")} · {format(new Date(date + "T00:00:00"), "d MMMM", { locale: dateLocale })} {time}–{String(parseInt(time) + 1).padStart(2, "0")}:00</div>
              <div><label className="chip text-[#A3A3A3]">{t("pt.book.noteOptional")}</label><textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="editorial-input dark w-full" data-testid="pt-form-note" /></div>
              <button type="submit" disabled={busy} className="w-full py-4 chip transition-colors" style={{ background: "var(--joh-accent)", color: "#0A0A0A" }} data-testid="pt-request-submit">
                {busy ? "…" : <>{t("pt.book.request").toUpperCase()} <ChevronRight size={13} className="inline"/></>}
              </button>
              <p className="chip text-[#4A4A4A]">{t("pt.book.manualReview")}</p>
            </form>
          )}
          {done && (
            <div className="border p-6" style={{ borderColor: "var(--joh-accent)" }} data-testid="pt-request-done">
              <div className="chip" style={{ color: "var(--joh-accent)" }}>✓ {t("pt.book.requestReceived").toUpperCase()}</div>
              <p className="text-sm text-[#DDD6CB] mt-3">{t("pt.book.requestReceivedBody", { date: done.date, time: done.time })}</p>
              <button type="button" onClick={() => setDone(null)} className="chip text-[#A3A3A3] underline underline-offset-4 mt-4" data-testid="pt-new-request">{t("pt.book.newRequest")}</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
